/**
 * EntityBadgeByIds — hydration wrapper that resolves a list of entity ids
 * from the CRM store and renders an EntityBadgeGroup with click translation.
 *
 * Callers only need to supply `entityIds` and `orgId`. The wrapper handles
 * batched store subscriptions, partial-success state, and per-id click
 * translation.
 *
 * Do NOT pass a `data` prop — use `EntityBadgeGroup` directly if you
 * already have the resolved data.
 */

import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCrmObjectSnapshots } from '@/lib/crm/hooks/useCrmObjectSnapshots';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';
import {
  getFailureVersion,
  hasFailedFetch,
  subscribeToFetchFailures,
} from '@/lib/crm/object-cards/entityFetchBatcher';
import { entityTypeToRouteSlug } from '@/lib/crm/utils/entityTypeUtils';

import { EntityBadgeGroup } from './EntityBadgeGroup';
import { buildEntityBadgeData } from './entityBadgeHelpers';
import type {
  ClickBehavior,
  EntityBadgeChangeMarker,
  EntityBadgeData,
  EntityBadgeGroupState,
  EntityBadgeGroupVariant,
} from './types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface EntityBadgeByIdsProps {
  entityIds: readonly string[];
  orgId: number;
  /**
   * Renders a group, so it takes the group's variant set — `record` is
   * excluded for the reason given on `EntityBadgeGroupVariant`. The
   * single-entity wrapper (`EntityBadgeById`) is the one that accepts it.
   */
  variant?: EntityBadgeGroupVariant;
  avatar?: boolean;
  interactive?: boolean;
  disabled?: boolean;
  /** How a click on each badge should navigate. Defaults to `'slideout'`. */
  clickBehavior?: ClickBehavior;
  /**
   * When supplied, overrides `clickBehavior` — called with the clicked
   * entity's id instead of performing default navigation.
   */
  onClick?: (id: string) => void;
  /** Maximum visible badges before overflow pill. Defaults to 3. */
  maxVisible?: number;
  /** Whether the overflow pill is expandable. Defaults to `true`. */
  expandable?: boolean;
  /**
   * Number of skeleton placeholders to render in `loading` state.
   * Defaults to 1.
   */
  loadingCount?: number;
  /**
   * When `false`, the row never wraps — the visible badge truncates with
   * ellipsis if it's too long. Forwarded to `EntityBadgeGroup`. Defaults
   * to `true` (current behavior).
   */
  wrap?: boolean;
  /** Additional className applied to the underlying group container. */
  className?: string;
  /**
   * Per-id pending-change markers, keyed by the SAME id passed in
   * `entityIds`. Ids absent from the map render unmarked. Supplied by
   * surfaces that show a proposal's projected state, so each association
   * reads as added / removed / untouched rather than as a flat list.
   */
  changeMarkers?: Readonly<Record<string, EntityBadgeChangeMarker>>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EntityBadgeByIds({
  entityIds,
  orgId,
  variant = 'default',
  avatar = false,
  interactive = true,
  disabled = false,
  clickBehavior = 'slideout',
  onClick,
  maxVisible,
  expandable,
  loadingCount,
  wrap,
  className,
  changeMarkers,
}: EntityBadgeByIdsProps): React.ReactElement | null {
  const snapshots = useCrmObjectSnapshots(entityIds, orgId);

  // Subscribe to fetch failure changes so partial/failed state reflects
  // fetch failures even when the store has no object for a given id.
  const failureVersion = useSyncExternalStore(
    subscribeToFetchFailures,
    getFailureVersion
  );

  const openSlideout = useOpenSlideoutOptional();
  const navigate = useNavigate();

  const handleDefaultClick = useCallback(
    (id: string) => {
      const object = snapshots.get(id);
      if (clickBehavior === 'slideout') {
        openSlideout?.(id);
      } else if (clickBehavior === 'page' && object) {
        navigate(
          `/${orgId}/crm/${entityTypeToRouteSlug(object.entity_type_name)}/${id}`
        );
      }
    },
    [clickBehavior, openSlideout, navigate, orgId, snapshots]
  );

  // Build the group render state, preserving the input order.
  const state = useMemo((): EntityBadgeGroupState => {
    if (entityIds.length === 0) {
      return { kind: 'data', data: [] };
    }

    let anyPending = false;
    const loaded: EntityBadgeData[] = [];
    let failedCount = 0;

    for (const id of entityIds) {
      const object = snapshots.get(id);
      if (object) {
        // Preserve the requested id (may differ from object.entity_id when a
        // merged alias resolves to a canonical entity) so click handlers and
        // React keys stay aligned with what the caller asked for.
        loaded.push({
          ...buildEntityBadgeData(object),
          id,
          changeMarker: changeMarkers?.[id],
        });
      } else if (hasFailedFetch(id)) {
        failedCount += 1;
      } else {
        anyPending = true;
      }
    }

    if (anyPending && loaded.length === 0 && failedCount === 0) {
      return { kind: 'loading' };
    }

    if (!anyPending && loaded.length === 0 && failedCount > 0) {
      return { kind: 'failed' };
    }

    if (failedCount > 0 || anyPending) {
      return { kind: 'partial', loaded, failedCount };
    }

    return { kind: 'data', data: loaded };
    // failureVersion is intentionally listed: hasFailedFetch() reads from a
    // store outside React, so we need to recompute when its version bumps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityIds, snapshots, failureVersion, changeMarkers]);

  const resolvedOnClick = useMemo(
    () =>
      onClick ?? (clickBehavior !== 'none' ? handleDefaultClick : undefined),
    [onClick, clickBehavior, handleDefaultClick]
  );

  return (
    <EntityBadgeGroup
      state={state}
      orgId={orgId}
      variant={variant}
      avatar={avatar}
      interactive={interactive}
      disabled={disabled}
      onClick={resolvedOnClick}
      maxVisible={maxVisible}
      expandable={expandable}
      loadingCount={loadingCount}
      wrap={wrap}
      className={className}
    />
  );
}
